import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, BarChart3, LogOut, Plus, Trash2, RefreshCw, Loader2, Car, Eye, Copy, 
  User, Award, CheckCircle2, File, Image, Building, Shield, DollarSign, Edit,
  TrendingUp, Menu, Filter, CheckCircle, XCircle, Pencil, X, Search, Package, AlertCircle,
  Settings, Camera, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ppf-auto-manager/artifacts/h7byux7s_UP-Logo-White.png";

const PPF_TYPES = [
  { id: 'GLOSS', name: 'GLOSS' },
  { id: 'MATT', name: 'MATT' },
  { id: 'COLOR_PPF', name: 'COLOR PPF' }
];

const COVERAGE_NAMES = {
  // New flattened items
  loading_edge: 'Loading Edge', door_edge: 'Door Edge',
  front_bumper: 'Front Bumper', hood: 'Hood', side_mirrors: 'Side Mirrors', fenders: 'Fenders',
  premium_front_bumper: 'Front Bumper (Premium)', premium_hood: 'Hood (Premium)', 
  premium_side_mirrors: 'Side Mirrors (Premium)', premium_fenders: 'Fenders (Premium)',
  side_skirts: 'Side Skirts', a_pillars: 'A-Pillars', roof_section: 'Roof Section',
  whole_car: 'Whole Car', headlights: 'Headlights', windshield: 'Windshield',
  // Interior items (NEW)
  interior_screens: 'Screens', interior_full: 'Full Interior',
  // Legacy items
  small_parts: 'Small Parts', front_package: 'Front Package', premium_package: 'Premium Package',
  complete_package: 'Complete Package', extras_headlights: 'Headlights', extras_windshield: 'Windshield'
};

// Sidebar
const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { path: '/admin', icon: BarChart3, label: 'Overview', exact: true },
    { path: '/admin/partners-overview', icon: TrendingUp, label: 'Partners Overview' },
    { path: '/admin/installations', icon: Car, label: 'All Installations' },
  ];
  
  // Inventory & Sales section (separated visually in sidebar)
  const inventorySalesItems = [
    { path: '/admin/inventory', icon: Package, label: 'PPF Inventory' },
    { path: '/admin/sales', icon: DollarSign, label: 'PPF Sales' },
  ];
  
  // Account Settings
  const accountSettingsItem = { path: '/admin/settings', icon: Settings, label: 'Account Settings' };
  
  // Partner Management is separate (at bottom)
  const partnerManagementItem = { path: '/admin/partners', icon: Users, label: 'Partner Management' };

  const handleLogout = () => { logout(); navigate('/'); toast.success('Logged out'); };
  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  const navContent = (
    <>
      <div className="p-3 md:p-4 border-b border-white/5">
        <Link to="/" className="flex items-center justify-center">
          <img src={LOGO_URL} alt="Unique Protection" className="h-12 md:h-14 w-auto" />
        </Link>
      </div>
      <div className="p-3 md:p-4 border-b border-white/5">
        <div className="bg-zinc-900/50 rounded-xl p-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white font-semibold text-sm truncate">{user?.name}</p>
              <p className="text-zinc-400 text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <span className="inline-block px-3 py-1 bg-red-600/20 text-red-400 text-sm font-medium rounded-full">
            Admin
          </span>
        </div>
      </div>
      <nav className="flex-1 p-3 md:p-4 overflow-y-auto">
        {/* Main Navigation */}
        <div className="space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-colors text-sm ${isActive(item.path, item.exact) ? 'bg-red-600/10 text-red-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'}`}>
              <item.icon className="w-4 h-4 md:w-5 md:h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
        
        {/* Separator before Inventory & Sales */}
        <div className="my-4 border-t border-zinc-800" />
        
        {/* Inventory & Sales Section */}
        <div className="space-y-1">
          {inventorySalesItems.map((item) => (
            <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-colors text-sm ${isActive(item.path, false) ? 'bg-red-600/10 text-red-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'}`}>
              <item.icon className="w-4 h-4 md:w-5 md:h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
        
        {/* Separator before Partner Management */}
        <div className="my-4 border-t border-zinc-800" />
        
        {/* Partner Management */}
        <div className="mt-2">
          <Link to={partnerManagementItem.path} onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-colors text-sm ${isActive(partnerManagementItem.path, false) ? 'bg-red-600/10 text-red-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'}`}>
            <partnerManagementItem.icon className="w-4 h-4 md:w-5 md:h-5" /><span className="font-medium">{partnerManagementItem.label}</span>
          </Link>
        </div>
        
        {/* Separator before Account Settings */}
        <div className="my-4 border-t border-zinc-800" />
        
        {/* Account Settings at bottom */}
        <Link to={accountSettingsItem.path} onClick={() => setMobileOpen(false)}
          className={`flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-colors text-sm ${isActive(accountSettingsItem.path, false) ? 'bg-red-600/10 text-red-400' : 'text-zinc-400 hover:bg-white/5 hover:text-white'}`}>
          <accountSettingsItem.icon className="w-4 h-4 md:w-5 md:h-5" /><span className="font-medium">{accountSettingsItem.label}</span>
        </Link>
      </nav>
      <div className="p-3 md:p-4 border-t border-white/5">
        <Button data-testid="logout-btn" onClick={handleLogout} variant="ghost" className="w-full justify-start gap-2 md:gap-3 text-zinc-400 hover:text-white hover:bg-white/5 h-9 md:h-10 text-sm">
          <LogOut className="w-4 h-4 md:w-5 md:h-5" /> Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile/Tablet portrait sticky header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#09090b] border-b border-white/10 z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-white">
          <Menu className="w-5 h-5" />
        </button>
        <img src={LOGO_URL} alt="Unique Protection" className="h-8 w-auto" />
      </div>
      
      {/* Mobile/Tablet portrait sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} />
      )}
      
      {/* Mobile/Tablet portrait sidebar */}
      <div className={`lg:hidden fixed top-0 left-0 h-screen w-64 bg-[#09090b] border-r border-white/5 z-50 transform transition-transform ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {navContent}
      </div>
      
      {/* Desktop sidebar (1024px+) */}
      <div className="hidden lg:flex w-64 bg-[#09090b] border-r border-white/5 h-screen flex-col">
        {navContent}
      </div>
    </>
  );
};

// Overview
const Overview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() });
        setStats(response.data);
      } catch (error) { toast.error('Failed to load stats'); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  // Calculate percentages for visual bars
  const total = stats?.total_installations || 1;
  const glossPct = Math.round(((stats?.by_ppf_type?.gloss || 0) / total) * 100);
  const mattPct = Math.round(((stats?.by_ppf_type?.matt || 0) / total) * 100);
  const colorPct = Math.round(((stats?.by_ppf_type?.color_ppf || 0) / total) * 100);
  const activePct = Math.round(((stats?.active_warranties || 0) / total) * 100);

  return (
    <div data-testid="admin-overview" className="space-y-4 lg:space-y-6">
      <h1 className="font-['Chivo'] text-xl lg:text-2xl font-bold text-white">Dashboard Overview</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 lg:p-5">
          <p className="text-zinc-400 text-[10px] lg:text-xs mb-0.5 lg:mb-1">Partners</p>
          <p className="font-['Chivo'] text-lg lg:text-2xl font-bold text-white">{stats?.total_partners || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 lg:p-5">
          <p className="text-zinc-400 text-[10px] lg:text-xs mb-0.5 lg:mb-1">Admins</p>
          <p className="font-['Chivo'] text-lg lg:text-2xl font-bold text-red-400">{stats?.total_admins || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 lg:p-5">
          <p className="text-zinc-400 text-[10px] lg:text-xs mb-0.5 lg:mb-1">Installations</p>
          <p className="font-['Chivo'] text-lg lg:text-2xl font-bold text-blue-400">{stats?.total_installations || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 lg:p-5">
          <p className="text-zinc-400 text-[10px] lg:text-xs mb-0.5 lg:mb-1">Active Warranties</p>
          <p className="font-['Chivo'] text-lg lg:text-2xl font-bold text-emerald-400">{stats?.active_warranties || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 lg:p-5 col-span-2 sm:col-span-1">
          <p className="text-zinc-400 text-[10px] lg:text-xs mb-0.5 lg:mb-1">Revenue</p>
          <p className="font-['Chivo'] text-lg lg:text-2xl font-bold text-amber-400">€{(stats?.total_revenue || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-6">
        {/* PPF Type Distribution with Visual Bars */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 lg:p-5">
          <h2 className="text-sm lg:text-base font-bold text-white mb-3 lg:mb-4">PPF Type Distribution</h2>
          <div className="space-y-2 lg:space-y-3">
            <div>
              <div className="flex justify-between text-[10px] lg:text-xs mb-1">
                <span className="text-zinc-400">GLOSS</span>
                <span className="text-white font-medium">{stats?.by_ppf_type?.gloss || 0} ({glossPct}%)</span>
              </div>
              <div className="h-1.5 lg:h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all" style={{width: `${glossPct}%`}}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-[10px] lg:text-xs mb-1">
                <span className="text-zinc-400">MATT</span>
                <span className="text-white font-medium">{stats?.by_ppf_type?.matt || 0} ({mattPct}%)</span>
              </div>
              <div className="h-1.5 lg:h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 rounded-full transition-all" style={{width: `${mattPct}%`}}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-[10px] lg:text-xs mb-1">
                <span className="text-zinc-400">COLOR PPF</span>
                <span className="text-white font-medium">{stats?.by_ppf_type?.color_ppf || 0} ({colorPct}%)</span>
              </div>
              <div className="h-1.5 lg:h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full transition-all" style={{width: `${colorPct}%`}}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Warranty Status with Visual */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 lg:p-5">
          <h2 className="text-sm lg:text-base font-bold text-white mb-3 lg:mb-4">Warranty Status</h2>
          <div className="flex items-center gap-3 lg:gap-6">
            {/* Circular Progress */}
            <div className="relative w-16 h-16 lg:w-24 lg:h-24 flex-shrink-0">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="50%" cy="50%" r="40%" className="fill-none stroke-zinc-800 stroke-[8]" />
                <circle cx="50%" cy="50%" r="40%" className="fill-none stroke-emerald-500 stroke-[8]" 
                  strokeDasharray={`${activePct * 2.51} 251`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-base lg:text-xl font-bold text-white">{activePct}%</span>
              </div>
            </div>
            <div className="space-y-1.5 lg:space-y-2 flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 lg:gap-2">
                  <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-emerald-500"></div>
                  <span className="text-zinc-400 text-[10px] lg:text-sm">Active</span>
                </div>
                <span className="text-white font-medium text-xs lg:text-sm">{stats?.active_warranties || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 lg:gap-2">
                  <div className="w-2 h-2 lg:w-2.5 lg:h-2.5 rounded-full bg-red-500"></div>
                  <span className="text-zinc-400 text-[10px] lg:text-sm">Expired</span>
                </div>
                <span className="text-white font-medium text-xs lg:text-sm">{(stats?.total_installations || 0) - (stats?.active_warranties || 0)}</span>
              </div>
              <div className="pt-1.5 lg:pt-2 border-t border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500 text-[10px] lg:text-xs">Total</span>
                  <span className="text-zinc-300 font-medium text-xs lg:text-sm">{stats?.total_installations || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Partner Management
const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newPartner, setNewPartner] = useState({ name: '', email: '', phone: '', address: '', company_name: '', company_code: '', vat_code: '', role: 'partner' });
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const { getAuthHeaders } = useAuth();

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partners`, { headers: getAuthHeaders() });
      setPartners(response.data);
    } catch (error) { toast.error('Failed to load partners'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPartners(); }, []);

  const handleAddPartner = async () => {
    if (!newPartner.name || !newPartner.email) { toast.error('Name and email required'); return; }
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/partners`, newPartner, { headers: getAuthHeaders() });
      setTempPassword(response.data.temp_password);
      toast.success('Partner created');
      fetchPartners();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create partner'); }
    finally { setSaving(false); }
  };

  const handleUpdatePartner = async () => {
    if (!selectedPartner) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/partners/${selectedPartner.id}`, {
        name: selectedPartner.name, email: selectedPartner.email, phone: selectedPartner.phone,
        address: selectedPartner.address, company_name: selectedPartner.company_name,
        company_code: selectedPartner.company_code, vat_code: selectedPartner.vat_code
      }, { headers: getAuthHeaders() });
      toast.success('Partner updated');
      setShowEditModal(false);
      fetchPartners();
    } catch (error) { toast.error('Failed to update partner'); }
    finally { setSaving(false); }
  };

  const handleChangeRole = async (partnerId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/partners/${partnerId}/role`, { role: newRole }, { headers: getAuthHeaders() });
      toast.success(`Role changed to ${newRole}`);
      fetchPartners();
    } catch (error) { toast.error('Failed to change role'); }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await axios.delete(`${API_URL}/api/partners/${id}`, { headers: getAuthHeaders() });
      toast.success('Partner deleted');
      fetchPartners();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleResetPassword = async (id) => {
    try {
      const response = await axios.post(`${API_URL}/api/partners/${id}/reset-password`, {}, { headers: getAuthHeaders() });
      toast.success(`New password: ${response.data.temp_password}`);
    } catch (error) { toast.error('Failed to reset password'); }
  };

  const closeAddModal = () => { setShowAddModal(false); setNewPartner({ name: '', email: '', phone: '', address: '', company_name: '', company_code: '', vat_code: '', role: 'partner' }); setTempPassword(''); };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  return (
    <div data-testid="admin-partners">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-8">
        <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">Partner Management</h1>
        <Button data-testid="add-partner-btn" onClick={() => setShowAddModal(true)} className="bg-red-600 hover:bg-red-700 text-white gap-2 h-9 text-sm">
          <Plus className="w-4 h-4" /> Add Partner
        </Button>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Representative</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden md:table-cell">Company</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden lg:table-cell">Contact</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Role</th>
                <th className="text-right p-3 text-zinc-400 font-medium text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {partner.avatar_base64 ? (
                        <img src={partner.avatar_base64} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-zinc-500" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-white font-medium text-sm truncate">{partner.name}</p>
                        <p className="text-zinc-500 text-xs truncate">{partner.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <p className="text-zinc-300 text-sm">{partner.company_name || '-'}</p>
                  </td>
                  <td className="p-3 hidden lg:table-cell">
                    <p className="text-zinc-300 text-sm">{partner.phone || '-'}</p>
                  </td>
                  <td className="p-3">
                    <Select value={partner.role} onValueChange={(v) => handleChangeRole(partner.id, v)}>
                      <SelectTrigger className="w-28 bg-zinc-950 border-zinc-700 text-white text-xs h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        <SelectItem value="partner" className="text-white text-sm">Partner</SelectItem>
                        <SelectItem value="demo_partner" className="text-white text-sm">Demo</SelectItem>
                        <SelectItem value="admin" className="text-white text-sm">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedPartner(partner); setShowEditModal(true); }} className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleResetPassword(partner.id)} className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePartner(partner.id)} className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {partners.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-500 text-sm">No partners found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Partner Modal */}
      <Dialog open={showAddModal} onOpenChange={closeAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-['Chivo'] text-xl">{tempPassword ? 'Partner Created' : 'Add New Partner'}</DialogTitle></DialogHeader>
          {tempPassword ? (
            <div className="py-4">
              <p className="text-zinc-400 mb-4">Partner created. Share these credentials:</p>
              <div className="bg-zinc-950 rounded-lg p-4 space-y-2 mb-4">
                <p className="text-sm"><span className="text-zinc-500">Email:</span> <span className="text-white">{newPartner.email}</span></p>
                <p className="text-sm"><span className="text-zinc-500">Password:</span> <span className="text-red-400 font-mono">{tempPassword}</span></p>
              </div>
              <Button onClick={closeAddModal} className="w-full bg-red-600 hover:bg-red-700">Done</Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-zinc-300">Representative Name *</Label><Input value={newPartner.name} onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  <div><Label className="text-zinc-300">Email *</Label><Input type="email" value={newPartner.email} onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  <div><Label className="text-zinc-300">Phone</Label><Input value={newPartner.phone} onChange={(e) => setNewPartner({ ...newPartner, phone: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  <div><Label className="text-zinc-300">Address</Label><Input value={newPartner.address} onChange={(e) => setNewPartner({ ...newPartner, address: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                </div>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-sm text-zinc-400 mb-4 flex items-center gap-2"><Building className="w-4 h-4" /> Company Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-zinc-300">Company Name</Label><Input value={newPartner.company_name} onChange={(e) => setNewPartner({ ...newPartner, company_name: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                    <div><Label className="text-zinc-300">Company Code</Label><Input value={newPartner.company_code} onChange={(e) => setNewPartner({ ...newPartner, company_code: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                    <div><Label className="text-zinc-300">VAT Code</Label><Input value={newPartner.vat_code} onChange={(e) => setNewPartner({ ...newPartner, vat_code: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                    <div>
                      <Label className="text-zinc-300">Role</Label>
                      <Select value={newPartner.role} onValueChange={(v) => setNewPartner({ ...newPartner, role: v })}>
                        <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          <SelectItem value="partner" className="text-white">Partner</SelectItem>
                          <SelectItem value="demo_partner" className="text-white">Demo Partner</SelectItem>
                          <SelectItem value="admin" className="text-white">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeAddModal} className="text-zinc-400">Cancel</Button>
                <Button onClick={handleAddPartner} disabled={saving} className="bg-red-600 hover:bg-red-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Partner Modal */}
      <Dialog open={showEditModal} onOpenChange={() => setShowEditModal(false)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-['Chivo'] text-xl">Edit Partner</DialogTitle></DialogHeader>
          {selectedPartner && (
            <>
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-zinc-300">Representative Name</Label><Input value={selectedPartner.name} onChange={(e) => setSelectedPartner({ ...selectedPartner, name: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  <div><Label className="text-zinc-300">Email</Label><Input type="email" value={selectedPartner.email} onChange={(e) => setSelectedPartner({ ...selectedPartner, email: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  <div><Label className="text-zinc-300">Phone</Label><Input value={selectedPartner.phone || ''} onChange={(e) => setSelectedPartner({ ...selectedPartner, phone: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  <div><Label className="text-zinc-300">Address</Label><Input value={selectedPartner.address || ''} onChange={(e) => setSelectedPartner({ ...selectedPartner, address: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                </div>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-sm text-zinc-400 mb-4 flex items-center gap-2"><Building className="w-4 h-4" /> Company Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-zinc-300">Company Name</Label><Input value={selectedPartner.company_name || ''} onChange={(e) => setSelectedPartner({ ...selectedPartner, company_name: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                    <div><Label className="text-zinc-300">Company Code</Label><Input value={selectedPartner.company_code || ''} onChange={(e) => setSelectedPartner({ ...selectedPartner, company_code: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                    <div><Label className="text-zinc-300">VAT Code</Label><Input value={selectedPartner.vat_code || ''} onChange={(e) => setSelectedPartner({ ...selectedPartner, vat_code: e.target.value })} className="mt-2 bg-zinc-950 border-zinc-700 text-white" /></div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowEditModal(false)} className="text-zinc-400">Cancel</Button>
                <Button onClick={handleUpdatePartner} disabled={saving} className="bg-red-600 hover:bg-red-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// All Installations (Admin view with full details including price, edit, delete)
const AllInstallations = () => {
  const [installations, setInstallations] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstall, setSelectedInstall] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [installToDelete, setInstallToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editPhotos, setEditPhotos] = useState([]);
  // Filter & Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ partner_id: '', ppf_type: '', warranty_status: '', warranty_check: '', date_from: '', date_to: '' });
  const [showFilters, setShowFilters] = useState(false);
  const { getAuthHeaders } = useAuth();

  const fetchInstallations = async () => {
    try {
      const [instRes, partRes] = await Promise.all([
        axios.get(`${API_URL}/api/installations`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/partners`, { headers: getAuthHeaders() })
      ]);
      setInstallations(instRes.data);
      setPartners(partRes.data);
    } catch (error) { toast.error('Failed to load installations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInstallations(); }, []);

  const loadPhotos = async (installId) => {
    try {
      const response = await axios.get(`${API_URL}/api/installations/${installId}/photos`, { headers: getAuthHeaders() });
      setPhotos(response.data);
    } catch (err) { setPhotos([]); }
  };

  const copyAccessCode = (code) => { navigator.clipboard.writeText(code); toast.success('Copied!'); };
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const getCoverageList = (coverage) => {
    if (!coverage) return [];
    return Object.entries(coverage).filter(([_, v]) => v).map(([k]) => COVERAGE_NAMES[k] || k);
  };

  // Filter and Search logic
  const filteredInstallations = installations.filter(inst => {
    // Search filter (includes vehicle info)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = 
        inst.vin?.toLowerCase().includes(term) ||
        inst.license_plate?.toLowerCase().includes(term) ||
        inst.client_name?.toLowerCase().includes(term) ||
        inst.access_code?.toLowerCase().includes(term) ||
        inst.brand?.toLowerCase().includes(term) ||
        inst.model?.toLowerCase().includes(term) ||
        `${inst.brand} ${inst.model}`.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    // Partner filter
    if (filters.partner_id && inst.partner_id !== filters.partner_id) return false;
    // PPF Type filter
    if (filters.ppf_type && inst.ppf_type !== filters.ppf_type) return false;
    // Warranty status filter
    if (filters.warranty_status === 'active' && !inst.warranty_active) return false;
    if (filters.warranty_status === 'expired' && inst.warranty_active) return false;
    // Warranty check filter (Admin read-only)
    if (filters.warranty_check && inst.warranty_check_status !== filters.warranty_check) return false;
    // Date range filter
    if (filters.date_from) {
      const instDate = new Date(inst.install_date);
      const fromDate = new Date(filters.date_from);
      if (instDate < fromDate) return false;
    }
    if (filters.date_to) {
      const instDate = new Date(inst.install_date);
      const toDate = new Date(filters.date_to);
      if (instDate > toDate) return false;
    }
    return true;
  });

  const totalRevenue = filteredInstallations.reduce((sum, i) => sum + (i.service_price || 0), 0);
  const clearFilters = () => setFilters({ partner_id: '', ppf_type: '', warranty_status: '', warranty_check: '', date_from: '', date_to: '' });
  const hasActiveFilters = filters.partner_id || filters.ppf_type || filters.warranty_status || filters.warranty_check || filters.date_from || filters.date_to;

  const openDetail = async (inst) => {
    setSelectedInstall(inst);
    if (inst.has_photos) await loadPhotos(inst.id);
  };

  const initiateDelete = (inst) => {
    setInstallToDelete(inst);
    setDeleteStep(1);
    setShowDeleteConfirm(true);
  };

  // Edit functions
  const initiateEdit = async (inst) => {
    setEditForm({
      id: inst.id,
      vin: inst.vin || '',
      brand: inst.brand || '',
      model: inst.model || '',
      year: inst.year || new Date().getFullYear(),
      original_color: inst.original_color || '',
      license_plate: inst.license_plate || '',
      client_name: inst.client_name || '',
      client_email: inst.client_email || '',
      client_phone: inst.client_phone || '',
      install_date: inst.install_date?.split('T')[0] || '',
      ppf_type: inst.ppf_type || 'GLOSS',
      ppf_color: inst.ppf_color || '',
      roll_number: inst.roll_number || '',  // Roll Number - Admin only
      coverage_areas: inst.coverage_areas || {},
      service_price: inst.service_price?.toString() || '',
      notes: inst.notes || '',
      warranty_service: inst.warranty_service || false,
      warranty_service_date: inst.warranty_service_date?.split('T')[0] || ''
    });
    // Load photos for editing
    if (inst.has_photos) {
      try {
        const response = await axios.get(`${API_URL}/api/installations/${inst.id}/photos`, { headers: getAuthHeaders() });
        setEditPhotos(response.data);
      } catch (err) { setEditPhotos([]); }
    } else {
      setEditPhotos([]);
    }
    setShowEditModal(true);
  };

  const handleUpdateInstallation = async () => {
    if (!editForm) return;
    
    // Basic validation
    if (!editForm.vin || !editForm.brand || !editForm.model || !editForm.client_name || !editForm.client_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        vin: editForm.vin,
        brand: editForm.brand,
        model: editForm.model,
        year: parseInt(editForm.year),
        original_color: editForm.original_color,
        license_plate: editForm.license_plate,
        client_name: editForm.client_name,
        client_email: editForm.client_email,
        client_phone: editForm.client_phone || null,
        install_date: editForm.install_date,
        ppf_type: editForm.ppf_type,
        ppf_color: editForm.ppf_type === 'COLOR_PPF' ? editForm.ppf_color : null,
        roll_number: editForm.roll_number || null,  // Roll Number - Admin only
        coverage_areas: editForm.coverage_areas,
        service_price: editForm.service_price ? parseFloat(editForm.service_price) : null,
        notes: editForm.notes || null,
        warranty_service: editForm.warranty_service || false,
        warranty_service_date: editForm.warranty_service_date || null
      };
      
      await axios.put(`${API_URL}/api/installations/${editForm.id}`, payload, { headers: getAuthHeaders() });
      toast.success('Installation updated successfully');
      setShowEditModal(false);
      setEditForm(null);
      setEditPhotos([]);
      fetchInstallations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update installation');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!editForm) return;
    try {
      await axios.delete(`${API_URL}/api/installations/${editForm.id}/photos/${photoId}`, { headers: getAuthHeaders() });
      setEditPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteWarrantyCard = async () => {
    if (!editForm) return;
    try {
      await axios.delete(`${API_URL}/api/installations/${editForm.id}/warranty-card`, { headers: getAuthHeaders() });
      toast.success('Warranty card deleted');
    } catch (error) {
      toast.error('Failed to delete warranty card');
    }
  };

  const toggleCoverage = (key) => {
    if (!editForm) return;
    setEditForm(prev => ({
      ...prev,
      coverage_areas: {
        ...prev.coverage_areas,
        [key]: !prev.coverage_areas[key]
      }
    }));
  };

  const handleDelete = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/installations/${installToDelete.id}`, { headers: getAuthHeaders() });
      toast.success('Installation deleted successfully');
      setShowDeleteConfirm(false);
      setInstallToDelete(null);
      setDeleteStep(1);
      fetchInstallations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete installation');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  return (
    <div data-testid="admin-installations" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">All Installations</h1>
        <div className="flex items-center gap-2">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 flex items-center gap-2">
            <span className="text-zinc-400 text-xs">{filteredInstallations.length} results</span>
            <span className="text-zinc-600">|</span>
            <DollarSign className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-amber-400 font-medium text-sm">€{totalRevenue.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-3">
        {/* Search - Always visible */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input 
              placeholder="Search VIN, plate, client, access code..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-zinc-900 border-zinc-700 text-white h-9 text-sm placeholder:text-zinc-500"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className={`h-9 px-3 border-zinc-700 ${hasActiveFilters ? 'bg-red-600/20 border-red-500/50 text-red-400' : 'bg-zinc-900 text-zinc-400'}`}
          >
            <Filter className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && <span className="ml-1.5 w-2 h-2 rounded-full bg-red-500"></span>}
          </Button>
        </div>

        {/* Filters - Collapsible on mobile */}
        {showFilters && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 md:p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {/* Partner Filter */}
              <Select value={filters.partner_id || 'all'} onValueChange={(v) => setFilters({...filters, partner_id: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-9 text-sm">
                  <SelectValue placeholder="Partner" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-white">All Partners</SelectItem>
                  {partners.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* PPF Type Filter */}
              <Select value={filters.ppf_type || 'all'} onValueChange={(v) => setFilters({...filters, ppf_type: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-9 text-sm">
                  <SelectValue placeholder="PPF Type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-white">All PPF Types</SelectItem>
                  <SelectItem value="GLOSS" className="text-white">GLOSS</SelectItem>
                  <SelectItem value="MATT" className="text-white">MATT</SelectItem>
                  <SelectItem value="COLOR_PPF" className="text-white">COLOR PPF</SelectItem>
                </SelectContent>
              </Select>

              {/* Warranty Status Filter */}
              <Select value={filters.warranty_status || 'all'} onValueChange={(v) => setFilters({...filters, warranty_status: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-9 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-white">All Status</SelectItem>
                  <SelectItem value="active" className="text-white">Active</SelectItem>
                  <SelectItem value="expired" className="text-white">Expired</SelectItem>
                </SelectContent>
              </Select>

              {/* Warranty Check Filter */}
              <Select value={filters.warranty_check || 'all'} onValueChange={(v) => setFilters({...filters, warranty_check: v === 'all' ? '' : v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-9 text-sm">
                  <SelectValue placeholder="Check" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all" className="text-white">All Checks</SelectItem>
                  <SelectItem value="pending" className="text-white">Pending</SelectItem>
                  <SelectItem value="overdue" className="text-white">Overdue</SelectItem>
                  <SelectItem value="completed" className="text-white">Completed</SelectItem>
                </SelectContent>
              </Select>

              {/* Date From */}
              <div>
                <Input 
                  type="date" 
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  className="bg-zinc-950 border-zinc-700 text-white h-9 text-sm"
                  placeholder="From"
                />
              </div>

              {/* Date To */}
              <div>
                <Input 
                  type="date" 
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  className="bg-zinc-950 border-zinc-700 text-white h-9 text-sm"
                  placeholder="To"
                />
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 hover:text-white h-8 text-xs">
                  <X className="w-3 h-3 mr-1" /> Clear all filters
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase">Vehicle</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase hidden lg:table-cell">Client</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase hidden xl:table-cell">Partner</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase">PPF</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase hidden xl:table-cell">Price</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase hidden sm:table-cell">Date</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase">Status</th>
                <th className="text-left p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase hidden md:table-cell">Warranty Check</th>
                <th className="text-right p-1.5 lg:p-3 text-zinc-400 font-medium text-[10px] lg:text-xs uppercase w-16 lg:w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filteredInstallations.map((inst) => (
                <tr key={inst.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-1.5 lg:p-3">
                    <p className="text-white font-medium text-xs lg:text-sm">{inst.brand} {inst.model}</p>
                    <p className="text-zinc-500 text-[10px] lg:text-xs">{inst.year}</p>
                    <p className="text-zinc-400 text-[10px] lg:hidden">{inst.partner_name || 'Unknown'}</p>
                  </td>
                  <td className="p-1.5 lg:p-3 hidden lg:table-cell">
                    <p className="text-zinc-300 text-sm">{inst.client_name}</p>
                  </td>
                  <td className="p-1.5 lg:p-3 text-zinc-400 text-sm hidden xl:table-cell">{inst.partner_name}</td>
                  <td className="p-1.5 lg:p-3">
                    <span className="text-white text-xs lg:text-sm">{inst.ppf_type?.replace('_', ' ')}</span>
                    <p className="text-amber-400 text-[10px] xl:hidden">{inst.service_price ? `€${inst.service_price.toLocaleString()}` : ''}</p>
                  </td>
                  <td className="p-1.5 lg:p-3 hidden xl:table-cell">
                    {inst.service_price ? (
                      <span className="text-amber-400 font-medium text-sm">€{inst.service_price.toLocaleString()}</span>
                    ) : <span className="text-zinc-600">-</span>}
                  </td>
                  <td className="p-1.5 lg:p-3 hidden sm:table-cell">
                    <span className="text-zinc-400 text-[10px] lg:text-xs">{new Date(inst.install_date).toLocaleDateString('en-GB', {day: '2-digit', month: 'short'})}</span>
                  </td>
                  <td className="p-1.5 lg:p-3">
                    <span className={`inline-flex items-center px-1 lg:px-1.5 py-0.5 rounded text-[10px] lg:text-xs font-medium ${inst.warranty_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {inst.warranty_active ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="p-1.5 lg:p-3 hidden md:table-cell">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] lg:text-xs font-medium ${
                      inst.warranty_check_status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                      inst.warranty_check_status === 'overdue' ? 'bg-red-500/10 text-red-400' :
                      'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {inst.warranty_check_status === 'completed' ? 'Completed' :
                       inst.warranty_check_status === 'overdue' ? 'Overdue' : 'Pending'}
                    </span>
                  </td>
                  <td className="p-1.5 lg:p-3 text-right">
                    <div className="flex justify-end gap-0">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(inst)} className="text-zinc-400 hover:text-white h-6 w-6 lg:h-7 lg:w-7 p-0">
                        <Eye className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => initiateEdit(inst)} className="text-zinc-400 hover:text-blue-400 h-6 w-6 lg:h-7 lg:w-7 p-0">
                        <Edit className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => initiateDelete(inst)} className="text-zinc-400 hover:text-red-400 h-6 w-6 lg:h-7 lg:w-7 p-0">
                        <Trash2 className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredInstallations.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-zinc-500 text-sm">
                  {searchTerm || hasActiveFilters ? 'No installations match your search/filters.' : 'No installations.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedInstall} onOpenChange={() => { setSelectedInstall(null); setPhotos([]); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Chivo'] text-xl">Installation Details (Admin)</DialogTitle></DialogHeader>
          {selectedInstall && (
            <div className="space-y-6">
              <div className="bg-red-600/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Access Code</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xl text-red-400">{selectedInstall.access_code}</span>
                  <Button size="sm" variant="ghost" onClick={() => copyAccessCode(selectedInstall.access_code)} className="text-red-400"><Copy className="w-4 h-4 mr-2" /> Copy</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <h3 className="text-sm text-zinc-400 mb-2 flex items-center gap-2"><Car className="w-4 h-4" /> Vehicle</h3>
                  <p className="text-white">{selectedInstall.year} {selectedInstall.brand} {selectedInstall.model}</p>
                  <p className="text-zinc-500 text-sm font-mono">{selectedInstall.vin}</p>
                  <p className="text-zinc-400 text-sm">{selectedInstall.original_color} • {selectedInstall.license_plate}</p>
                </div>
                <div className="bg-zinc-950/50 rounded-lg p-4">
                  <h3 className="text-sm text-zinc-400 mb-2 flex items-center gap-2"><User className="w-4 h-4" /> Client</h3>
                  <p className="text-white">{selectedInstall.client_name}</p>
                  <p className="text-zinc-500 text-sm">{selectedInstall.client_email}</p>
                  {selectedInstall.client_phone && <p className="text-zinc-400 text-sm">{selectedInstall.client_phone}</p>}
                </div>
              </div>

              <div className="bg-zinc-950/50 rounded-lg p-4">
                <h3 className="text-sm text-zinc-400 mb-3 flex items-center gap-2"><Award className="w-4 h-4" /> Installation & Warranty</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-zinc-500">Partner</p><p className="text-white">{selectedInstall.partner_name}</p></div>
                  <div><p className="text-zinc-500">Date</p><p className="text-white">{formatDate(selectedInstall.install_date)}</p></div>
                  <div><p className="text-zinc-500">PPF Type</p><p className="text-white">{selectedInstall.ppf_type?.replace('_', ' ')}{selectedInstall.ppf_color && ` - ${selectedInstall.ppf_color}`}</p></div>
                  <div><p className="text-zinc-500">Warranty</p><p className="text-white">{selectedInstall.warranty_years} Years</p></div>
                  <div><p className="text-zinc-500">Expires</p><p className={selectedInstall.warranty_active ? 'text-emerald-400' : 'text-red-400'}>{formatDate(selectedInstall.warranty_expiration)}</p></div>
                  <div><p className="text-zinc-500">Service Price</p><p className="text-amber-400 font-medium">{selectedInstall.service_price ? `€${selectedInstall.service_price.toLocaleString()}` : '-'}</p></div>
                  {/* Roll Number - Admin Only */}
                  <div><p className="text-zinc-500">Roll Number</p><p className="text-white font-mono">{selectedInstall.roll_number || '-'}</p></div>
                </div>
              </div>

              <div>
                <h3 className="text-sm text-zinc-400 mb-2">Coverage Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {getCoverageList(selectedInstall.coverage_areas).map((item) => (
                    <span key={item} className="px-3 py-1 bg-zinc-800 rounded-full text-sm text-zinc-300">{item}</span>
                  ))}
                  {getCoverageList(selectedInstall.coverage_areas).length === 0 && <span className="text-zinc-500 text-sm">No coverage data</span>}
                </div>
              </div>

              {selectedInstall.notes && (
                <div><h3 className="text-sm text-zinc-400 mb-2">Notes</h3><p className="text-zinc-300 text-sm bg-zinc-950/50 rounded-lg p-3">{selectedInstall.notes}</p></div>
              )}

              {/* Warranty Service Status - View Only (Admin edits in Edit modal) */}
              <div className={`rounded-lg p-4 ${
                selectedInstall.warranty_service 
                  ? 'bg-emerald-950/20 border-2 border-emerald-500/30' 
                  : 'bg-amber-950/10 border-2 border-dashed border-amber-500/30'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      selectedInstall.warranty_service 
                        ? 'bg-emerald-600/20' 
                        : 'bg-amber-600/20'
                    }`}>
                      {selectedInstall.warranty_service ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-zinc-300 flex items-center gap-2">
                        <Shield className="w-4 h-4" /> Warranty Service
                      </p>
                      <p className={`text-sm font-semibold ${
                        selectedInstall.warranty_service ? 'text-emerald-400' : 'text-amber-400'
                      }`}>
                        {selectedInstall.warranty_service ? 'Completed' : 'Pending'}
                      </p>
                    </div>
                  </div>
                  {selectedInstall.warranty_service && selectedInstall.warranty_service_date && (
                    <p className="text-emerald-400/70 text-xs">
                      {new Date(selectedInstall.warranty_service_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>

              {photos.length > 0 && (
                <div>
                  <h3 className="text-sm text-zinc-400 mb-2 flex items-center gap-2"><Image className="w-4 h-4" /> Photos</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {photos.map((p, i) => (<img key={i} src={p.data} alt="" className="aspect-square rounded-lg object-cover" />))}
                  </div>
                </div>
              )}

              <div className="flex gap-4 text-sm">
                {selectedInstall.has_warranty_card && <span className="flex items-center gap-1 text-emerald-400"><File className="w-4 h-4" /> Warranty Card</span>}
                {selectedInstall.has_photos && <span className="flex items-center gap-1 text-red-400"><Image className="w-4 h-4" /> {selectedInstall.photo_count} Photos</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Two-Step Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { if (!open) { setShowDeleteConfirm(false); setDeleteStep(1); setInstallToDelete(null); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-red-400">
              {deleteStep === 1 ? 'Delete Installation?' : 'Confirm Permanent Deletion'}
            </DialogTitle>
          </DialogHeader>
          {installToDelete && (
            <div className="space-y-4">
              <div className="bg-zinc-950/50 rounded-lg p-4">
                <p className="text-white font-medium">{installToDelete.year} {installToDelete.brand} {installToDelete.model}</p>
                <p className="text-zinc-500 text-sm font-mono">{installToDelete.vin}</p>
                <p className="text-zinc-400 text-sm">Client: {installToDelete.client_name}</p>
              </div>
              
              {deleteStep === 1 ? (
                <p className="text-zinc-300 text-sm">Are you sure you want to delete this installation? This will remove all associated data including warranty information and photos.</p>
              ) : (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 font-medium mb-2">⚠️ This action is irreversible</p>
                  <p className="text-zinc-300 text-sm">This installation record, warranty data, and all photos will be permanently deleted. This cannot be undone.</p>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => { setShowDeleteConfirm(false); setDeleteStep(1); }} className="text-zinc-400">
                  Cancel
                </Button>
                <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {deleteStep === 1 ? 'Yes, Delete' : 'Confirm Delete'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Installation Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { if (!open) { setShowEditModal(false); setEditForm(null); setEditPhotos([]); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">Edit Installation</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-6">
              {/* Vehicle Info */}
              <div className="bg-zinc-950/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2"><Car className="w-4 h-4" /> Vehicle Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">VIN *</Label>
                    <Input value={editForm.vin} onChange={(e) => setEditForm({ ...editForm, vin: e.target.value.toUpperCase() })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm font-mono" maxLength={17} />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Brand *</Label>
                    <Input value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Model *</Label>
                    <Input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Year</Label>
                    <Input type="number" value={editForm.year} onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" min="1900" max="2030" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Original Color</Label>
                    <Input value={editForm.original_color} onChange={(e) => setEditForm({ ...editForm, original_color: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">License Plate</Label>
                    <Input value={editForm.license_plate} onChange={(e) => setEditForm({ ...editForm, license_plate: e.target.value.toUpperCase() })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="bg-zinc-950/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2"><User className="w-4 h-4" /> Client Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">Full Name *</Label>
                    <Input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Email *</Label>
                    <Input type="email" value={editForm.client_email} onChange={(e) => setEditForm({ ...editForm, client_email: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Phone</Label>
                    <Input value={editForm.client_phone} onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                </div>
              </div>

              {/* Installation Details */}
              <div className="bg-zinc-950/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-4 flex items-center gap-2"><Award className="w-4 h-4" /> Installation Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">Install Date</Label>
                    <Input type="date" value={editForm.install_date} onChange={(e) => setEditForm({ ...editForm, install_date: e.target.value })}
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">PPF Type</Label>
                    <Select value={editForm.ppf_type} onValueChange={(v) => setEditForm({ ...editForm, ppf_type: v, ppf_color: v !== 'COLOR_PPF' ? '' : editForm.ppf_color })}>
                      <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        {PPF_TYPES.map(t => <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {editForm.ppf_type === 'COLOR_PPF' && (
                    <div>
                      <Label className="text-zinc-400 text-xs">PPF Color</Label>
                      <Input value={editForm.ppf_color} onChange={(e) => setEditForm({ ...editForm, ppf_color: e.target.value })}
                        className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" placeholder="e.g., Midnight Blue" />
                    </div>
                  )}
                  <div>
                    <Label className="text-zinc-400 text-xs">Service Price (€)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">€</span>
                      <Input type="number" step="0.01" value={editForm.service_price} onChange={(e) => setEditForm({ ...editForm, service_price: e.target.value })}
                        className="pl-6 bg-zinc-900 border-zinc-700 text-white h-9 text-sm" />
                    </div>
                  </div>
                  {/* Roll Number - Admin Only */}
                  <div>
                    <Label className="text-zinc-400 text-xs">Roll Number</Label>
                    <Input value={editForm.roll_number} onChange={(e) => setEditForm({ ...editForm, roll_number: e.target.value })}
                      placeholder="e.g., ROLL-001"
                      className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9 text-sm font-mono" />
                  </div>
                </div>
              </div>

              {/* Coverage Areas */}
              <div className="bg-zinc-950/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-4">Coverage Areas</h3>
                <div className="space-y-4">
                  {/* Small Parts */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Small Parts</p>
                    <div className="flex flex-wrap gap-3">
                      {[{id: 'loading_edge', name: 'Loading Edge'}, {id: 'door_edge', name: 'Door Edge'}].map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox checked={!!editForm.coverage_areas[item.id]} onCheckedChange={() => toggleCoverage(item.id)} id={`edit-${item.id}`} />
                          <Label htmlFor={`edit-${item.id}`} className="text-zinc-300 text-sm cursor-pointer">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Front Package */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Front Package</p>
                    <div className="flex flex-wrap gap-3">
                      {[{id: 'front_bumper', name: 'Front Bumper'}, {id: 'hood', name: 'Hood'}, {id: 'side_mirrors', name: 'Side Mirrors'}, {id: 'fenders', name: 'Fenders'}].map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox checked={!!editForm.coverage_areas[item.id]} onCheckedChange={() => toggleCoverage(item.id)} id={`edit-${item.id}`} />
                          <Label htmlFor={`edit-${item.id}`} className="text-zinc-300 text-sm cursor-pointer">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Premium Package */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Premium Package</p>
                    <div className="flex flex-wrap gap-3">
                      {[{id: 'premium_front_bumper', name: 'Front Bumper'}, {id: 'premium_hood', name: 'Hood'}, {id: 'premium_side_mirrors', name: 'Side Mirrors'}, {id: 'premium_fenders', name: 'Fenders'}, {id: 'side_skirts', name: 'Side Skirts'}, {id: 'a_pillars', name: 'A-Pillars'}, {id: 'roof_section', name: 'Roof Section'}].map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox checked={!!editForm.coverage_areas[item.id]} onCheckedChange={() => toggleCoverage(item.id)} id={`edit-${item.id}`} />
                          <Label htmlFor={`edit-${item.id}`} className="text-zinc-300 text-sm cursor-pointer">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Complete Package */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Complete Package</p>
                    <div className="flex flex-wrap gap-3">
                      {[{id: 'whole_car', name: 'Whole Car'}].map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox checked={!!editForm.coverage_areas[item.id]} onCheckedChange={() => toggleCoverage(item.id)} id={`edit-${item.id}`} />
                          <Label htmlFor={`edit-${item.id}`} className="text-zinc-300 text-sm cursor-pointer">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Extras */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Extras</p>
                    <div className="flex flex-wrap gap-3">
                      {[{id: 'headlights', name: 'Headlights'}, {id: 'windshield', name: 'Windshield'}].map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox checked={!!editForm.coverage_areas[item.id]} onCheckedChange={() => toggleCoverage(item.id)} id={`edit-${item.id}`} />
                          <Label htmlFor={`edit-${item.id}`} className="text-zinc-300 text-sm cursor-pointer">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Interior */}
                  <div>
                    <p className="text-xs text-zinc-500 mb-2">Interior</p>
                    <div className="flex flex-wrap gap-3">
                      {[{id: 'interior_screens', name: 'Screens'}, {id: 'interior_full', name: 'Full Interior'}].map(item => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox checked={!!editForm.coverage_areas[item.id]} onCheckedChange={() => toggleCoverage(item.id)} id={`edit-${item.id}`} />
                          <Label htmlFor={`edit-${item.id}`} className="text-zinc-300 text-sm cursor-pointer">{item.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Warranty Service Toggle */}
              <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-zinc-300 text-sm font-medium">Warranty Service</Label>
                    <p className="text-zinc-500 text-xs mt-0.5">Post-installation inspection completed</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {editForm.warranty_service && (
                      <Input 
                        type="date" 
                        value={editForm.warranty_service_date || ''} 
                        onChange={(e) => setEditForm({ ...editForm, warranty_service_date: e.target.value })}
                        className="w-36 bg-zinc-950 border-zinc-700 text-white text-sm h-8"
                      />
                    )}
                    <Checkbox 
                      checked={!!editForm.warranty_service} 
                      onCheckedChange={(checked) => setEditForm({ 
                        ...editForm, 
                        warranty_service: checked,
                        warranty_service_date: checked && !editForm.warranty_service_date 
                          ? new Date().toISOString().split('T')[0] 
                          : editForm.warranty_service_date
                      })} 
                      id="edit-warranty-service" 
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-zinc-400 text-xs">Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-700 text-white text-sm min-h-[80px]" placeholder="Additional notes..." />
              </div>

              {/* Existing Photos (View & Delete) */}
              {editPhotos.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2"><Image className="w-4 h-4" /> Photos ({editPhotos.length})</h3>
                  <div className="grid grid-cols-4 gap-3">
                    {editPhotos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <img src={photo.data} alt="" className="aspect-square rounded-lg object-cover" />
                        <button onClick={() => handleDeletePhoto(photo.id)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-600/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">Hover over a photo and click X to delete it.</p>
                </div>
              )}

              <DialogFooter className="gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="ghost" onClick={() => { setShowEditModal(false); setEditForm(null); setEditPhotos([]); }} className="text-zinc-400">
                  Cancel
                </Button>
                <Button onClick={handleUpdateInstallation} disabled={saving} className="bg-red-600 hover:bg-red-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// PPF Inventory Component - Aggregate by PPF Type
const PPFInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustType, setAdjustType] = useState('damaged');
  const [adjustQty, setAdjustQty] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const { getAuthHeaders } = useAuth();

  const emptyForm = {
    ppf_type: '', color: '', quantity_type: 'meters', quantity_to_add: '', unit_cost: '', notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchInventory(); fetchSummary(); }, []);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory`, { headers: getAuthHeaders() });
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory/summary`, { headers: getAuthHeaders() });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ppf_type || !form.quantity_to_add) {
      toast.error('Select PPF type and enter quantity'); return;
    }
    if (form.ppf_type === 'COLOR_PPF' && !form.color) {
      toast.error('Color is required for Color PPF'); return;
    }
    setSaving(true);
    try {
      if (editingItem) {
        // Update existing
        await axios.put(`${API_URL}/api/inventory/${editingItem.id}`, {
          total_quantity: parseFloat(form.quantity_to_add),
          unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : null,
          notes: form.notes || null
        }, { headers: getAuthHeaders() });
        toast.success('Inventory updated');
        setShowEditModal(false);
      } else {
        // Add new (or add to existing)
        await axios.post(`${API_URL}/api/inventory`, {
          ppf_type: form.ppf_type,
          color: form.ppf_type === 'COLOR_PPF' ? form.color : null,
          quantity_type: form.quantity_type,
          quantity_to_add: parseFloat(form.quantity_to_add),
          unit_cost: form.unit_cost ? parseFloat(form.unit_cost) : null,
          notes: form.notes || null
        }, { headers: getAuthHeaders() });
        toast.success('Inventory added');
        setShowAddModal(false);
      }
      setForm(emptyForm);
      setEditingItem(null);
      fetchInventory();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save inventory');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjust = async () => {
    if (!adjustQty || parseFloat(adjustQty) <= 0) {
      toast.error('Enter a valid quantity'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/inventory/${adjustItem.id}/adjust?adjustment_type=${adjustType}&quantity=${adjustQty}`, {}, { headers: getAuthHeaders() });
      toast.success(`${adjustType === 'damaged' ? 'Damaged' : 'Returned'} quantity recorded`);
      setShowAdjustModal(false);
      setAdjustItem(null);
      setAdjustQty('');
      fetchInventory();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      ppf_type: item.ppf_type, color: item.color || '', quantity_type: item.quantity_type,
      quantity_to_add: item.total_quantity.toString(), unit_cost: item.unit_cost?.toString() || '',
      notes: item.notes || ''
    });
    setShowEditModal(true);
  };

  const openAdjust = (item, type) => {
    setAdjustItem(item);
    setAdjustType(type);
    setAdjustQty('');
    setShowAdjustModal(true);
  };

  const initiateDelete = (item) => { setDeleteTarget(item); setDeleteStep(1); };

  const handleDelete = async () => {
    if (deleteStep === 1) { setDeleteStep(2); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/inventory/${deleteTarget.id}`, { headers: getAuthHeaders() });
      toast.success('Inventory deleted');
      setDeleteTarget(null);
      setDeleteStep(0);
      fetchInventory();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  // Add form JSX
  const addFormJSX = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">PPF Type *</Label>
          <Select value={form.ppf_type} onValueChange={(v) => setForm(prev => ({ ...prev, ppf_type: v, color: v !== 'COLOR_PPF' ? '' : prev.color }))}>
            <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm">
              <SelectValue placeholder="Select PPF Type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {PPF_TYPES.map(t => <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">Quantity Type *</Label>
          <Select value={form.quantity_type} onValueChange={(v) => setForm(prev => ({ ...prev, quantity_type: v }))}>
            <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="meters" className="text-white">Meters (linear)</SelectItem>
              <SelectItem value="rolls" className="text-white">Rolls</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {form.ppf_type === 'COLOR_PPF' && (
        <div>
          <Label className="text-zinc-300 text-sm">Color *</Label>
          <Input value={form.color} onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
            className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="e.g., Midnight Blue" />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">{editingItem ? 'Total Quantity *' : 'Quantity to Add *'}</Label>
          <Input type="number" step={form.quantity_type === 'rolls' ? '1' : '0.1'} value={form.quantity_to_add} 
            onChange={(e) => setForm(prev => ({ ...prev, quantity_to_add: e.target.value }))}
            className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" 
            placeholder={form.quantity_type === 'rolls' ? '5' : '50'} />
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">Unit Cost (€)</Label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
            <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm(prev => ({ ...prev, unit_cost: e.target.value }))}
              className="pl-7 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="100.00" />
          </div>
        </div>
      </div>
      <div>
        <Label className="text-zinc-300 text-sm">Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
          className="mt-1.5 bg-zinc-950 border-zinc-700 text-white text-sm min-h-[60px]" placeholder="Additional notes..." />
      </div>
    </div>
  );

  return (
    <div data-testid="ppf-inventory" className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">PPF Inventory</h1>
        <Button onClick={() => { setForm(emptyForm); setEditingItem(null); setShowAddModal(true); }} className="bg-red-600 hover:bg-red-700 text-white gap-2 h-10 text-sm">
          <Plus className="w-4 h-4" /> Add Stock
        </Button>
      </div>

      {/* Summary Cards by PPF Type */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {['GLOSS', 'MATT', 'COLOR_PPF'].map(ppfType => {
            const data = summary.by_ppf_type?.[ppfType] || {};
            return (
              <div key={ppfType} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <h3 className="font-['Chivo'] text-lg font-semibold text-white mb-3">{ppfType.replace('_', ' ')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-zinc-500 text-xs">Total</p>
                    <p className="text-white font-medium">{(data.total || 0).toLocaleString()} m</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Used</p>
                    <p className="text-blue-400 font-medium">{(data.used || 0).toLocaleString()} m</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Damaged</p>
                    <p className="text-red-400 font-medium">{(data.damaged || 0).toLocaleString()} m</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Remaining</p>
                    <p className={`font-medium ${(data.remaining || 0) > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>{(data.remaining || 0).toLocaleString()} m</p>
                  </div>
                </div>
                {ppfType === 'COLOR_PPF' && data.colors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <p className="text-zinc-500 text-xs mb-2">Colors:</p>
                    <div className="flex flex-wrap gap-1">
                      {data.colors.map((c, i) => (
                        <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">{c.color}: {c.remaining}m</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Total Value */}
      {summary && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-sm">Total Inventory Value</span>
            <span className="font-['Chivo'] text-2xl font-bold text-amber-400">€{(summary.total_value || 0).toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 md:p-4 text-zinc-400 font-medium text-xs uppercase">PPF Type</th>
                <th className="text-left p-3 md:p-4 text-zinc-400 font-medium text-xs uppercase">Total</th>
                <th className="text-left p-3 md:p-4 text-zinc-400 font-medium text-xs uppercase hidden sm:table-cell">Used</th>
                <th className="text-left p-3 md:p-4 text-zinc-400 font-medium text-xs uppercase hidden md:table-cell">Damaged</th>
                <th className="text-left p-3 md:p-4 text-zinc-400 font-medium text-xs uppercase">Remaining</th>
                <th className="text-right p-3 md:p-4 text-zinc-400 font-medium text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map((item) => (
                <tr key={item.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-3 md:p-4">
                    <span className="text-white font-medium">{item.ppf_type?.replace('_', ' ')}</span>
                    {item.color && <span className="text-zinc-400 text-sm ml-2">({item.color})</span>}
                  </td>
                  <td className="p-3 md:p-4">
                    <span className="text-zinc-300">{item.total_quantity} {item.quantity_type === 'rolls' ? 'rolls' : 'm'}</span>
                  </td>
                  <td className="p-3 md:p-4 hidden sm:table-cell">
                    <span className="text-blue-400">{item.used_quantity || 0} {item.quantity_type === 'rolls' ? 'rolls' : 'm'}</span>
                  </td>
                  <td className="p-3 md:p-4 hidden md:table-cell">
                    <span className="text-red-400">{item.damaged_quantity || 0} {item.quantity_type === 'rolls' ? 'rolls' : 'm'}</span>
                  </td>
                  <td className="p-3 md:p-4">
                    <span className={`font-medium ${item.remaining_quantity > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {item.remaining_quantity} {item.quantity_type === 'rolls' ? 'rolls' : 'm'}
                    </span>
                  </td>
                  <td className="p-3 md:p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="text-zinc-400 hover:text-white h-8 w-8 p-0" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openAdjust(item, 'damaged')} className="text-red-400 hover:text-red-300 h-8 w-8 p-0" title="Record Damaged">
                        <XCircle className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openAdjust(item, 'returned')} className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0" title="Record Returned">
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => initiateDelete(item)} className="text-zinc-500 hover:text-red-400 h-8 w-8 p-0" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {inventory.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-zinc-500">
                  No inventory items. Add stock for each PPF type!
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">Add PPF Stock</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {addFormJSX}
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)} className="text-zinc-400">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Add Stock
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">Edit Inventory</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {addFormJSX}
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)} className="text-zinc-400">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Modal (Damaged/Returned) */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">
              Record {adjustType === 'damaged' ? 'Damaged' : 'Returned'} Stock
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-400 text-sm mb-4">
              {adjustItem?.ppf_type?.replace('_', ' ')} {adjustItem?.color ? `(${adjustItem.color})` : ''}
            </p>
            <Label className="text-zinc-300 text-sm">Quantity ({adjustItem?.quantity_type === 'rolls' ? 'rolls' : 'meters'})</Label>
            <Input type="number" step="0.1" value={adjustQty} onChange={(e) => setAdjustQty(e.target.value)}
              className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="Enter quantity" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdjustModal(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleAdjust} disabled={saving} className={adjustType === 'damaged' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteStep > 0} onOpenChange={() => { setDeleteStep(0); setDeleteTarget(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">
              {deleteStep === 1 ? 'Delete Inventory?' : 'Confirm Permanent Deletion'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {deleteStep === 1 ? (
              <p className="text-zinc-400">
                Delete <span className="text-white">{deleteTarget?.ppf_type?.replace('_', ' ')}</span> inventory?
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-red-400 font-medium">This action cannot be undone.</p>
                <p className="text-zinc-400">All inventory data for this PPF type will be permanently removed.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleteStep(0); setDeleteTarget(null); }} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {deleteStep === 1 ? 'Delete' : 'Yes, Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// PPF Sales Component - Full CRUD
const PPFSales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ partner_id: '', ppf_type: '', paid: '' });
  const [partners, setPartners] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [saving, setSaving] = useState(false);
  // Two-step delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { getAuthHeaders } = useAuth();
  
  const emptyForm = {
    partner_id: '', ppf_type: '', color: '',
    roll_number: '', quantity_type: 'meters', quantity: '', service_price: '', paid: false, 
    sale_date: new Date().toISOString().split('T')[0], notes: ''
  };
  const [form, setForm] = useState(emptyForm);

  const fetchSales = async () => {
    try {
      const [salesRes, partnersRes] = await Promise.all([
        axios.get(`${API_URL}/api/sales`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/partners`, { headers: getAuthHeaders() })
      ]);
      setSales(salesRes.data);
      setPartners(partnersRes.data);
    } catch (error) { toast.error('Failed to load sales data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSales(); }, []);

  const filteredSales = sales.filter(sale => {
    if (filters.partner_id && sale.partner_id !== filters.partner_id) return false;
    if (filters.ppf_type && sale.ppf_type !== filters.ppf_type) return false;
    if (filters.paid === 'paid' && !sale.paid) return false;
    if (filters.paid === 'unpaid' && sale.paid) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ppf_type || !form.roll_number || !form.quantity || !form.service_price) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.ppf_type === 'COLOR_PPF' && !form.color) {
      toast.error('Color is required for COLOR PPF'); return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ppf_type: form.ppf_type,
        color: form.ppf_type === 'COLOR_PPF' ? form.color : null,
        roll_number: form.roll_number,
        quantity_type: form.quantity_type,
        quantity: parseFloat(form.quantity),
        service_price: parseFloat(form.service_price),
        paid: form.paid,
        sale_date: form.sale_date,
        notes: form.notes,
        partner_id: form.partner_id || undefined
      };
      
      if (editingSale) {
        await axios.put(`${API_URL}/api/sales/${editingSale.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Sale updated successfully');
      } else {
        await axios.post(`${API_URL}/api/sales`, payload, { headers: getAuthHeaders() });
        toast.success('Sale created successfully');
      }
      
      setShowAddModal(false);
      setShowEditModal(false);
      setEditingSale(null);
      setForm(emptyForm);
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sale');
    } finally { setSaving(false); }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setForm({
      partner_id: sale.partner_id,
      ppf_type: sale.ppf_type,
      color: sale.color || '',
      roll_number: sale.roll_number,
      quantity_type: sale.quantity_type || 'meters',
      quantity: sale.quantity.toString(),
      service_price: sale.service_price.toString(),
      paid: sale.paid,
      sale_date: (sale.sale_date || sale.install_date)?.split('T')[0] || '',
      notes: sale.notes || ''
    });
    setShowEditModal(true);
  };

  const initiateDeleteSale = (sale) => {
    setSaleToDelete(sale);
    setDeleteStep(1);
    setShowDeleteConfirm(true);
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;
    
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/sales/${saleToDelete.id}`, { headers: getAuthHeaders() });
      toast.success('Sale deleted');
      setShowDeleteConfirm(false);
      setDeleteStep(1);
      setSaleToDelete(null);
      fetchSales();
    } catch (error) { 
      toast.error('Failed to delete sale'); 
    } finally {
      setDeleting(false);
    }
  };

  const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.service_price || 0), 0);
  const paidCount = filteredSales.filter(s => s.paid).length;
  const unpaidCount = filteredSales.filter(s => !s.paid).length;
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  // Sale form JSX - inlined to prevent input focus loss on re-render
  const saleFormJSX = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">Partner *</Label>
          <Select value={form.partner_id || 'self'} onValueChange={(v) => setForm(prev => ({ ...prev, partner_id: v === 'self' ? '' : v }))}>
            <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm">
              <SelectValue placeholder="Select Partner" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="self" className="text-white">Self (Admin)</SelectItem>
              {partners.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">PPF Type *</Label>
          <Select value={form.ppf_type} onValueChange={(v) => setForm(prev => ({ ...prev, ppf_type: v, color: v !== 'COLOR_PPF' ? '' : prev.color }))}>
            <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm">
              <SelectValue placeholder="Select PPF Type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {PPF_TYPES.map(t => <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {form.ppf_type === 'COLOR_PPF' && (
        <div>
          <Label className="text-zinc-300 text-sm">Color *</Label>
          <Input value={form.color} onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
            className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="e.g., Midnight Blue" />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">Roll Number *</Label>
          <Input value={form.roll_number} onChange={(e) => setForm(prev => ({ ...prev, roll_number: e.target.value }))}
            className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="ROLL-001" />
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">Service Price (€) *</Label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
            <Input type="number" step="0.01" value={form.service_price} onChange={(e) => setForm(prev => ({ ...prev, service_price: e.target.value }))}
              className="pl-7 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" placeholder="500.00" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-300 text-sm">Quantity Type *</Label>
          <Select value={form.quantity_type} onValueChange={(v) => setForm(prev => ({ ...prev, quantity_type: v }))}>
            <SelectTrigger className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="meters" className="text-white">Meters (linear)</SelectItem>
              <SelectItem value="rolls" className="text-white">Rolls</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">Quantity ({form.quantity_type === 'rolls' ? 'rolls' : 'meters'}) *</Label>
          <Input type="number" step={form.quantity_type === 'rolls' ? '1' : '0.1'} value={form.quantity} 
            onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))}
            className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" 
            placeholder={form.quantity_type === 'rolls' ? '2' : '5.5'} />
        </div>
        <div>
          <Label className="text-zinc-300 text-sm">Sales Date *</Label>
          <Input type="date" value={form.sale_date} onChange={(e) => setForm(prev => ({ ...prev, sale_date: e.target.value }))}
            className="mt-1.5 bg-zinc-950 border-zinc-700 text-white h-10 text-sm" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Checkbox checked={form.paid} onCheckedChange={(checked) => setForm(prev => ({ ...prev, paid: checked }))} id="paid" />
        <Label htmlFor="paid" className="text-zinc-300 text-sm cursor-pointer">Paid</Label>
      </div>
      <div>
        <Label className="text-zinc-300 text-sm">Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
          className="mt-1.5 bg-zinc-950 border-zinc-700 text-white text-sm min-h-[60px]" placeholder="Additional notes..." />
      </div>
    </div>
  );

  return (
    <div data-testid="admin-ppf-sales" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">PPF Sales</h1>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => { setForm(emptyForm); setShowAddModal(true); }} className="bg-red-600 hover:bg-red-700 text-white gap-2 h-9 text-sm">
            <Plus className="w-4 h-4" /> New Sale
          </Button>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <span className="text-zinc-400 text-xs">Total:</span>
            <span className="text-amber-400 font-bold text-sm">€{totalRevenue.toLocaleString()}</span>
          </div>
          <div className="bg-emerald-900/30 border border-emerald-800/50 rounded-lg px-3 py-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 font-medium text-sm">{paidCount} Paid</span>
          </div>
          <div className="bg-red-900/30 border border-red-800/50 rounded-lg px-3 py-2 flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 font-medium text-sm">{unpaidCount} Unpaid</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-zinc-400" />
          <span className="text-sm text-zinc-400">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select value={filters.partner_id || 'all'} onValueChange={(v) => setFilters({ ...filters, partner_id: v === 'all' ? '' : v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="All Partners" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-white">All Partners</SelectItem>
              {partners.map(p => <SelectItem key={p.id} value={p.id} className="text-white">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.ppf_type || 'all'} onValueChange={(v) => setFilters({ ...filters, ppf_type: v === 'all' ? '' : v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="All PPF Types" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-white">All PPF Types</SelectItem>
              {PPF_TYPES.map(t => <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.paid || 'all'} onValueChange={(v) => setFilters({ ...filters, paid: v === 'all' ? '' : v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-10 text-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="paid" className="text-emerald-400">Paid</SelectItem>
              <SelectItem value="unpaid" className="text-red-400">Unpaid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Partner</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">PPF</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden lg:table-cell">Color</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden md:table-cell">Roll</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Qty</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Price</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase">Status</th>
                <th className="text-left p-3 text-zinc-400 font-medium text-xs uppercase hidden lg:table-cell">Sales Date</th>
                <th className="text-right p-3 text-zinc-400 font-medium text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((sale) => (
                <tr key={sale.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-3"><p className="text-white text-sm">{sale.partner_name}</p></td>
                  <td className="p-3 text-white text-sm">{sale.ppf_type?.replace('_', ' ')}</td>
                  <td className="p-3 text-zinc-400 text-sm hidden lg:table-cell">{sale.color || '-'}</td>
                  <td className="p-3 font-mono text-zinc-400 text-xs hidden md:table-cell">{sale.roll_number}</td>
                  <td className="p-3 text-zinc-300 text-sm">{sale.quantity} {sale.quantity_type === 'rolls' ? 'rolls' : 'm'}</td>
                  <td className="p-3 text-amber-400 font-medium text-sm">€{sale.service_price?.toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${sale.paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {sale.paid ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td className="p-3 text-zinc-500 text-xs hidden lg:table-cell">{formatDate(sale.sale_date || sale.install_date)}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(sale)} className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => initiateDeleteSale(sale)} className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSales.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-zinc-500">No sales found. Click &quot;New Sale&quot; to add one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Sale Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">Add New Sale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {saleFormJSX}
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setShowAddModal(false)} className="text-zinc-400">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Create Sale
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-white">Edit Sale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            {saleFormJSX}
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)} className="text-zinc-400">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Update Sale
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Two-Step Delete Confirmation Modal for Sales */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { if (!open) { setShowDeleteConfirm(false); setDeleteStep(1); setSaleToDelete(null); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-xl text-red-400">
              {deleteStep === 1 ? 'Delete Sale?' : 'Confirm Permanent Deletion'}
            </DialogTitle>
          </DialogHeader>
          {saleToDelete && (
            <div className="space-y-4">
              <div className="bg-zinc-950/50 rounded-lg p-4">
                <p className="text-white font-medium">{saleToDelete.ppf_type?.replace('_', ' ')} - {saleToDelete.roll_number}</p>
                <p className="text-zinc-500 text-sm">Partner: {saleToDelete.partner_name}</p>
                <p className="text-amber-400 text-sm">€{saleToDelete.service_price?.toLocaleString()}</p>
              </div>
              
              {deleteStep === 1 ? (
                <p className="text-zinc-300 text-sm">Are you sure you want to delete this sale record? This will remove all associated data.</p>
              ) : (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 font-medium mb-2">⚠️ This action is irreversible</p>
                  <p className="text-zinc-300 text-sm">This sale record will be permanently deleted. This cannot be undone.</p>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => { setShowDeleteConfirm(false); setDeleteStep(1); }} className="text-zinc-400">
                  Cancel
                </Button>
                <Button onClick={handleDeleteSale} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {deleteStep === 1 ? 'Yes, Delete' : 'Confirm Delete'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Partners Overview Component
const PartnersOverview = () => {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [partnerInstallations, setPartnerInstallations] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/partners-overview`, { headers: getAuthHeaders() });
        setOverview(response.data);
      } catch (error) { toast.error('Failed to load partners overview'); }
      finally { setLoading(false); }
    };
    fetchOverview();
  }, []);

  const toggleExpand = async (partnerId) => {
    if (expandedPartner === partnerId) {
      setExpandedPartner(null);
      setPartnerInstallations([]);
      return;
    }
    
    setExpandedPartner(partnerId);
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API_URL}/api/installations`, { headers: getAuthHeaders() });
      const partnerInst = response.data.filter(inst => inst.partner_id === partnerId);
      setPartnerInstallations(partnerInst);
    } catch (error) {
      toast.error('Failed to load partner details');
    } finally {
      setLoadingDetails(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;

  const totalRevenue = overview.reduce((sum, p) => sum + p.total_revenue, 0);
  const totalInstallations = overview.reduce((sum, p) => sum + p.total_installations, 0);

  // Calculate analytics for expanded partner
  const getPartnerAnalytics = (partnerId) => {
    const partner = overview.find(p => p.id === partnerId);
    const installs = partnerInstallations;
    
    // Monthly trend (last 6 months)
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStr = date.toLocaleDateString('en-US', { month: 'short' });
      const count = installs.filter(inst => {
        const instDate = new Date(inst.install_date);
        return instDate.getMonth() === date.getMonth() && instDate.getFullYear() === date.getFullYear();
      }).length;
      months.push({ month: monthStr, count });
    }
    
    // Active warranties
    const activeWarranties = installs.filter(inst => inst.warranty_active).length;
    
    // Average per install
    const avgRevenue = partner?.total_installations > 0 ? 
      (partner.total_revenue / partner.total_installations).toFixed(0) : 0;
    
    return { months, activeWarranties, avgRevenue };
  };

  return (
    <div data-testid="admin-partners-overview" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Chivo'] text-xl md:text-2xl font-bold text-white">Partners Overview</h1>
          <p className="text-zinc-500 text-sm mt-1">Click a partner card to see detailed analytics</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-red-400" />
            <span className="text-zinc-400 text-xs md:text-sm">Partners:</span>
            <span className="text-white font-bold text-sm">{overview.length}</span>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 flex items-center gap-2">
            <Car className="w-4 h-4 text-emerald-400" />
            <span className="text-zinc-400 text-xs md:text-sm">Total Installs:</span>
            <span className="text-emerald-400 font-bold text-sm">{totalInstallations}</span>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <span className="text-zinc-400 text-xs md:text-sm">Total Revenue:</span>
            <span className="text-amber-400 font-bold text-sm">€{totalRevenue.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Partners Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {overview.map((partner) => {
          const isExpanded = expandedPartner === partner.id;
          const analytics = isExpanded ? getPartnerAnalytics(partner.id) : null;
          
          return (
            <motion.div key={partner.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className={`bg-zinc-900 border rounded-xl overflow-hidden transition-all ${isExpanded ? 'border-red-500/50' : 'border-zinc-800 hover:border-zinc-700'}`}>
              
              {/* Main Card */}
              <div className="p-4 md:p-5 cursor-pointer" onClick={() => toggleExpand(partner.id)}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{partner.name}</p>
                      <p className="text-zinc-500 text-sm">{partner.company_name || 'Independent'}</p>
                    </div>
                  </div>
                  <div className={`p-2 rounded-lg transition-colors ${isExpanded ? 'bg-red-600/20' : 'bg-zinc-800'}`}>
                    <Eye className={`w-4 h-4 ${isExpanded ? 'text-red-400' : 'text-zinc-400'}`} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-zinc-950/50 rounded-lg p-3 text-center">
                    <p className="text-amber-400 font-bold text-xl">€{partner.total_revenue.toLocaleString()}</p>
                    <p className="text-zinc-500 text-xs">Revenue</p>
                  </div>
                  <div className="bg-zinc-950/50 rounded-lg p-3 text-center">
                    <p className="text-white font-bold text-xl">{partner.total_installations}</p>
                    <p className="text-zinc-500 text-xs">Installs</p>
                  </div>
                  <div className="bg-zinc-950/50 rounded-lg p-3 text-center">
                    <p className="text-emerald-400 font-bold text-xl">{partner.cars_wrapped}</p>
                    <p className="text-zinc-500 text-xs">Active</p>
                  </div>
                </div>

                {Object.keys(partner.ppf_breakdown || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(partner.ppf_breakdown).map(([type, count]) => (
                      <span key={type} className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-400">
                        {type?.replace('_', ' ')}: <span className="text-white">{count}</span>
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs">
                  <span className="text-zinc-500">{partner.email}</span>
                  {partner.phone && <span className="text-zinc-500">{partner.phone}</span>}
                </div>
              </div>

              {/* Expanded Analytics Section */}
              {isExpanded && (
                <div className="border-t border-zinc-800 bg-zinc-950/30 p-4 md:p-5">
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-red-500" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h4 className="text-white font-semibold text-sm">Detailed Analytics</h4>
                      
                      {/* Additional Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-zinc-900 rounded-lg p-3">
                          <p className="text-emerald-400 font-bold text-lg">{analytics?.activeWarranties || 0}</p>
                          <p className="text-zinc-500 text-xs">Active Warranties</p>
                        </div>
                        <div className="bg-zinc-900 rounded-lg p-3">
                          <p className="text-blue-400 font-bold text-lg">€{analytics?.avgRevenue || 0}</p>
                          <p className="text-zinc-500 text-xs">Avg per Install</p>
                        </div>
                        <div className="bg-zinc-900 rounded-lg p-3">
                          <p className="text-red-400 font-bold text-lg">{partner.total_installations - (analytics?.activeWarranties || 0)}</p>
                          <p className="text-zinc-500 text-xs">Expired Warranties</p>
                        </div>
                        <div className="bg-zinc-900 rounded-lg p-3">
                          <p className="text-amber-400 font-bold text-lg">{((partner.total_installations / Math.max(totalInstallations, 1)) * 100).toFixed(0)}%</p>
                          <p className="text-zinc-500 text-xs">Market Share</p>
                        </div>
                      </div>

                      {/* Monthly Trend */}
                      {analytics?.months && (
                        <div>
                          <p className="text-zinc-400 text-xs mb-2">Monthly Installations (Last 6 Months)</p>
                          <div className="flex items-end gap-1 h-16">
                            {analytics.months.map((m, i) => {
                              const maxCount = Math.max(...analytics.months.map(x => x.count), 1);
                              const height = (m.count / maxCount) * 100;
                              return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                  <div className="w-full bg-zinc-800 rounded-t relative" style={{ height: '48px' }}>
                                    <div 
                                      className="absolute bottom-0 w-full bg-red-600/60 rounded-t transition-all"
                                      style={{ height: `${Math.max(height, 5)}%` }}
                                    />
                                  </div>
                                  <span className="text-zinc-500 text-[10px]">{m.month}</span>
                                  <span className="text-white text-xs font-medium">{m.count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Recent Installations */}
                      {partnerInstallations.length > 0 && (
                        <div>
                          <p className="text-zinc-400 text-xs mb-2">Recent Installations</p>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {partnerInstallations.slice(0, 5).map(inst => (
                              <div key={inst.id} className="flex items-center justify-between bg-zinc-900 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <Car className="w-4 h-4 text-zinc-400" />
                                  <span className="text-white text-sm">{inst.brand} {inst.model}</span>
                                  <span className="text-zinc-500 text-xs">({inst.ppf_type?.replace('_', ' ')})</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-amber-400 text-sm">€{inst.service_price || 0}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${inst.warranty_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                                    {inst.warranty_active ? 'Active' : 'Expired'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}
        {overview.length === 0 && (
          <div className="col-span-full p-8 text-center text-zinc-500">No partners found.</div>
        )}
      </div>
    </div>
  );
};

// ==================== ADMIN ACCOUNT SETTINGS ====================
const AdminAccountSettings = () => {
  const { user, token, refreshUser } = useAuth();
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    company_name: user?.company_name || '',
    company_code: user?.company_code || '',
    vat_code: user?.vat_code || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_base64 || '');
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        company_name: user.company_name || '',
        company_code: user.company_code || '',
        vat_code: user.vat_code || ''
      });
      setAvatarPreview(user.avatar_base64 || '');
    }
  }, [user]);
  
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 500 * 1024) {
      toast.error('Avatar must be less than 500KB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      setAvatarPreview(reader.result);
      try {
        await axios.put(`${API_URL}/api/profile`, { avatar_base64: reader.result }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        refreshUser();
        toast.success('Avatar updated');
      } catch (err) {
        toast.error('Failed to update avatar');
      }
    };
    reader.readAsDataURL(file);
  };
  
  const handleProfileSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshUser();
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/profile/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <h1 className="font-['Chivo'] text-2xl text-white flex items-center gap-2">
        <Settings className="w-6 h-6 text-red-500" /> Account Settings
      </h1>
      
      {/* Avatar Section */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h2 className="font-['Chivo'] text-lg text-white mb-4 flex items-center gap-2">
          <Camera className="w-5 h-5 text-red-500" /> Profile Photo
        </h2>
        <div className="flex items-center gap-6">
          <div className="relative">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Avatar" className="w-24 h-24 rounded-full object-cover border-2 border-zinc-700" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center">
                <User className="w-10 h-10 text-zinc-500" />
              </div>
            )}
            <label className="absolute bottom-0 right-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors">
              <Camera className="w-4 h-4 text-white" />
              <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
            </label>
          </div>
          <div>
            <p className="text-zinc-400 text-sm">Upload a new avatar</p>
            <p className="text-zinc-500 text-xs mt-1">Max 500KB, JPG or PNG</p>
          </div>
        </div>
      </div>
      
      {/* Profile Information */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h2 className="font-['Chivo'] text-lg text-white mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-red-500" /> Profile Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">Name</Label>
            <Input value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
              className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">Email</Label>
            <Input value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
              className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">Phone</Label>
            <Input value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
              className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">Address</Label>
            <Input value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
              className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
        </div>
        <Button onClick={handleProfileSave} disabled={saving} className="mt-4 bg-red-600 hover:bg-red-700">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
        </Button>
      </div>
      
      {/* Password Change */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h2 className="font-['Chivo'] text-lg text-white mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-red-500" /> Change Password
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">Current Password</Label>
            <div className="relative">
              <Input type={showCurrentPassword ? 'text' : 'password'} value={passwordForm.current_password}
                onChange={e => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                className="bg-zinc-950 border-zinc-700 text-white pr-10" />
              <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {showCurrentPassword ? <XCircle className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">New Password</Label>
            <div className="relative">
              <Input type={showNewPassword ? 'text' : 'password'} value={passwordForm.new_password}
                onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                className="bg-zinc-950 border-zinc-700 text-white pr-10" />
              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {showNewPassword ? <XCircle className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-sm">Confirm New Password</Label>
            <div className="relative">
              <Input type={showConfirmPassword ? 'text' : 'password'} value={passwordForm.confirm_password}
                onChange={e => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                className="bg-zinc-950 border-zinc-700 text-white pr-10" />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                {showConfirmPassword ? <XCircle className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <Button onClick={handlePasswordChange} disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password}
          className="mt-4 bg-red-600 hover:bg-red-700">
          {changingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</> : 'Change Password'}
        </Button>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  return (
    <div className="min-h-screen bg-[#09090b] lg:flex flex-col" data-testid="admin-dashboard">
      <div className="flex-1 lg:flex">
        <Sidebar />
        <main className="flex-1 p-4 lg:p-8 overflow-auto pt-16 lg:pt-8 pb-12">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="partners-overview" element={<PartnersOverview />} />
            <Route path="installations" element={<AllInstallations />} />
            <Route path="inventory" element={<PPFInventory />} />
            <Route path="sales" element={<PPFSales />} />
            <Route path="partners" element={<Partners />} />
            <Route path="settings" element={<AdminAccountSettings />} />
          </Routes>
        </main>
      </div>
      {/* Footer */}
      <footer className="bg-[#09090b] border-t border-white/5 py-3 text-center">
        <p className="text-zinc-500 text-xs">© 2026 Unique Protection. All rights reserved.</p>
      </footer>
    </div>
  );
}
